import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/useRoles";
import { Permission, PermissionValue } from "@/types";
import { isRelationalManager, isBasicUser } from "@/lib/rbac";
import {
  Users,
  Calculator,
  CreditCard,
  Phone,
  GraduationCap,
  Send,
  Building2,
  Database,
  Shield,
  LogOut,
  MessageCircle,
  Info,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ToolItem {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  route: string;
  requiredPermission?: PermissionValue;
  rmVisible?: boolean; // visible to relational_manager
  basicVisible?: boolean; // visible to basic_user
}

const tools: ToolItem[] = [
  {
    id: "channel-partners",
    label: "Channel Partners",
    icon: Users,
    color: "bg-primary/10 text-primary",
    route: "/tools/channel-partners",
    rmVisible: false,
  },
  {
    id: "dsa-partners",
    label: "DSA Partners",
    icon: Users,
    color: "bg-primary/10 text-primary",
    route: "/tools/dsa-partners",
    rmVisible: true,
  },
  {
    id: "emi",
    label: "EMI Calculator",
    icon: Calculator,
    color: "bg-accent/10 text-accent",
    route: "/tools/emi-calculator",
    rmVisible: true,
    basicVisible: true,
  },
  {
    id: "cibil",
    label: "CIBIL Check",
    icon: CreditCard,
    color: "bg-warning/10 text-warning",
    route: "/tools/cibil-check",
    rmVisible: false,
  },
  {
    id: "contact",
    label: "Banker Contact",
    icon: Phone,
    color: "bg-success/10 text-success",
    route: "/tools/sm-contact",
    rmVisible: false,
  },
  {
    id: "banks",
    label: "Banks",
    icon: Building2,
    color: "bg-info/10 text-info",
    route: "/products",
    rmVisible: false,
  },
  {
    id: "training",
    label: "Training",
    icon: GraduationCap,
    color: "bg-info/10 text-info",
    route: "/tools/training",
    rmVisible: false,
  },
  {
    id: "broadcast",
    label: "Broadcast",
    icon: Send,
    color: "bg-gold/10 text-gold",
    route: "/tools/broadcast",
    requiredPermission: Permission.SHOW_IN_BROADCAST,
    rmVisible: false,
  },
  {
    id: "payouts",
    label: "Payouts & DSA",
    icon: CreditCard,
    color: "bg-accent/10 text-accent",
    route: "/tools/payouts",
    rmVisible: false,
  },
  {
    id: "knowledge",
    label: "Knowledge",
    icon: GraduationCap,
    color: "bg-info/10 text-info",
    route: "/tools/knowledge-sharing",
    rmVisible: false,
  },
  {
    id: "backend",
    label: "Backend",
    icon: Database,
    color: "bg-destructive/10 text-destructive",
    route: "/tools/backend",
    requiredPermission: Permission.ACCESS_BACKEND_TOOLS,
    rmVisible: false,
  },
  {
    id: "admin",
    label: "Admin",
    icon: Shield,
    color: "bg-primary/10 text-primary",
    route: "/tools/admin",
    requiredPermission: Permission.ACCESS_ADMIN_TOOLS,
    rmVisible: false,
  },
  {
    id: "network-map",
    label: "Network Map",
    icon: MapPin,
    color: "bg-info/10 text-info",
    route: "/tools/network-map",
    requiredPermission: Permission.ACCESS_ADMIN_TOOLS,
    rmVisible: false,
  },
  {
    id: "askbot",
    label: "AskBot",
    icon: MessageCircle,
    color: "bg-violet-500/10 text-violet-600",
    route: "/tools/askbot",
    rmVisible: false,
  },
];

// Dev/staging-only tile. Shown to every role on non-production builds (bypasses
// the RBAC filter below) so any tester can verify exactly what build is running.
const buildInfoTile: ToolItem = {
  id: "build-info",
  label: "Build Info",
  icon: Info,
  color: "bg-orange-500/10 text-orange-600",
  route: "/tools/build-info",
  rmVisible: true,
};

export default function Tools() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions(user?.roles || []);
  const isRM = isRelationalManager(user);
  const isBasic = isBasicUser(user);

  // Check if running in dev or staging environment using explicit env variable
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";
  const isStaging = import.meta.env.VITE_ENVIRONMENT === "staging";

  // Filter tools based on user permissions and role
  const visibleTools = tools.filter((tool) => {
    // Basic User only sees basicVisible tools (EMI Calculator); logout lives in the page header
    if (isBasic) return tool.basicVisible === true;
    // Relational manager only sees rmVisible tools
    if (isRM) return tool.rmVisible === true;
    // Others: check permission
    if (!tool.requiredPermission) return true;
    return hasPermission(tool.requiredPermission);
  });

  // On dev/staging builds, surface the Build Info tile to every role (bypasses
  // the RBAC filter above) so any tester can inspect what build is running.
  if (isDevelopment || isStaging) {
    visibleTools.push(buildInfoTile);
  }

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully", {
        description: "You have been logged out of your account.",
      });
      navigate("/");
    } catch (error) {
      toast.error("Logout failed", {
        description: "An error occurred while logging out.",
      });
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 pt-6 max-w-7xl mx-auto relative">
        {/* Dev/Staging Environment Watermark */}
        {(isDevelopment || isStaging) && (
          <div className="fixed inset-0 z-0 pointer-events-none flex flex-wrap content-center justify-center overflow-hidden gap-x-24 gap-y-32 p-8">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "text-5xl font-black -rotate-45 whitespace-nowrap",
                  isDevelopment ? "text-orange-400/10" : "text-yellow-400/10",
                )}
              >
                {isDevelopment ? "DEV BUILD" : "STAGING BUILD"}
              </div>
            ))}
          </div>
        )}

        <PageHeader
          title="Tools"
          subtitle="Resources to help you close deals faster"
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex-shrink-0 h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-colors"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Logout
            </Button>
          }
        />

        {visibleTools.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-muted-foreground text-sm">No tools available for your role.</p>
          </div>
        ) : (
        <div
          className={cn(
            "grid gap-3 md:gap-4 relative z-10",
            isRM || isBasic ? "grid-cols-2" : "grid-cols-3 sm:grid-cols-4",
          )}
        >
          {visibleTools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 active:scale-95"
                onClick={() => navigate(tool.route)}
              >
                <CardContent className="p-4 md:p-6 flex flex-col items-center text-center gap-2 md:gap-3">
                  <div className={cn("p-3 md:p-4 rounded-xl", tool.color)}>
                    <tool.icon className="h-6 w-6 md:h-8 md:w-8" />
                  </div>
                  <span className="text-xs md:text-sm font-medium">
                    {tool.label}
                  </span>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
        )}
      </div>
    </AppLayout>
  );
}